import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import AppModal from "../../../components/AppModal";
import { i18n } from "../../../translate/i18n";
import apiV2 from "../../../services/apiV2";
import { AuthContext } from "../../../context/Auth/AuthContext";
import useGoogleCalendarOAuth from "../hooks/useGoogleCalendarOAuth";

export default function IntegrationConfigModal({ open, integration, onClose, onSaved }) {
	const [apiKey, setApiKey] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const { user } = useContext(AuthContext);
	const {
		connect: connectGoogle,
		disconnect: disconnectGoogle,
		loading: googleLoading,
	} = useGoogleCalendarOAuth();

	useEffect(() => {
		if (open) {
			setApiKey("");
		}
	}, [open, integration?.id]);

	if (!integration) {
		return null;
	}

	const isAsaas = integration.id === "asaas";
	const isGoogleCalendar = integration.id === "google-calendar";
	const name = i18n.t(integration.nameKey);
	const isGoogleConnected = isGoogleCalendar && integration.status === "connected";
	const busy = isLoading || googleLoading;

	const handleSave = async () => {
		if (isAsaas && !apiKey.trim()) return;

		const payload = { name: integration.id, apiKey: apiKey.trim(), companyId: user.companyId };
		setIsLoading(true);
		try {
			const { data } = await apiV2.post("/integrations", payload);
			toast.success(data.success || i18n.t("integrations.modal.saveSuccess"));
			await onSaved?.();
			onClose();
		} catch (error) {
			toast.error(
				error.response?.data?.error ||
				i18n.t("integrations.modal.saveError")
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleConnectGoogle = async () => {
		try {
			await connectGoogle(async () => {
				await onSaved?.();
			});
			onClose();
		} catch (error) {
			toast.error(
				error.response?.data?.error ||
				i18n.t("integrations.googleCalendar.connectError")
			);
		}
	};

	const handleDisconnectGoogle = async () => {
		try {
			await disconnectGoogle();
			toast.success(i18n.t("integrations.googleCalendar.disconnectSuccess"));
			await onSaved?.();
			onClose();
		} catch (error) {
			toast.error(
				error.response?.data?.error ||
				i18n.t("integrations.googleCalendar.disconnectError")
			);
		}
	};

	const footer = (() => {
		if (isGoogleCalendar) {
			return (
				<div className="flex flex-wrap justify-end gap-2">
					<button type="button" className="btn btn-sub-gray" onClick={onClose} disabled={busy}>
						{i18n.t("integrations.modal.cancel")}
					</button>
					{isGoogleConnected ? (
						<button
							type="button"
							className="btn bg-danger text-white"
							onClick={handleDisconnectGoogle}
							disabled={busy}
						>
							{busy
								? i18n.t("integrations.googleCalendar.disconnecting")
								: i18n.t("integrations.googleCalendar.disconnect")}
						</button>
					) : (
						<button
							type="button"
							className="btn bg-primary text-white"
							onClick={handleConnectGoogle}
							disabled={busy}
						>
							{busy
								? i18n.t("integrations.googleCalendar.connecting")
								: i18n.t("integrations.modal.connectGoogle")}
						</button>
					)}
				</div>
			);
		}

		return (
			<div className="flex flex-wrap justify-end gap-2">
				<button type="button" className="btn btn-sub-gray" onClick={onClose}>
					{i18n.t("integrations.modal.cancel")}
				</button>
				<button
					type="button"
					className="btn bg-primary text-white"
					onClick={handleSave}
					disabled={busy || (isAsaas && !apiKey.trim())}
				>
					{i18n.t("integrations.modal.save")}
				</button>
			</div>
		);
	})();

	return (
		<AppModal
			open={open}
			onClose={onClose}
			title={i18n.t("integrations.modal.title", { name })}
			subtitle={i18n.t(integration.descriptionKey)}
			footer={footer}
		>
			{isAsaas && (
				<div className="space-y-4">
					<p className="m-0 text-sm text-default-500 dark:text-default-400">
						{i18n.t("integrations.asaas.modalHint")}
					</p>
					<label className="block">
						<span className="mb-2 block text-sm font-medium text-default-900 dark:text-default-100">
							{i18n.t("integrations.asaas.apiKeyLabel")}
						</span>
						<input
							type="text"
							className="form-input w-full"
							placeholder={i18n.t("integrations.asaas.apiKeyPlaceholder")}
							value={apiKey}
							onChange={(e) => setApiKey(e.target.value)}
						/>
					</label>
				</div>
			)}

			{isGoogleCalendar && (
				<div className="space-y-4">
					<p className="m-0 text-sm text-default-500 dark:text-default-400">
						{i18n.t("integrations.googleCalendar.modalHint")}
					</p>
					{isGoogleConnected ? (
						<div className="rounded-xl border border-default-200 bg-default-50 px-4 py-5 dark:border-default-700 dark:bg-default-900">
							<p className="m-0 text-sm font-medium text-success">
								{i18n.t("integrations.status.connected")}
							</p>
							{integration.config?.email && (
								<p className="mt-2 mb-0 text-sm text-default-700 dark:text-default-200">
									{integration.config.email}
								</p>
							)}
							<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">
								{i18n.t("integrations.googleCalendar.connectedHint")}
							</p>
						</div>
					) : (
						<div className="rounded-xl border border-dashed border-default-200 bg-default-50 px-4 py-6 text-center dark:border-default-700 dark:bg-default-900">
							<p className="m-0 text-sm text-default-500 dark:text-default-400">
								{i18n.t("integrations.googleCalendar.connectHint")}
							</p>
						</div>
					)}
				</div>
			)}
		</AppModal>
	);
}
